import React from 'react';
import { useWrapRequest } from 'use-wrap-request';
import { observer } from 'mobx-react';
import { Alert, AlertDescription } from '@components/ui/alert';
import { useSearchReducer } from '../../../components/hooks/useSearchReducer';
import { ActivityDto } from '@api/FitnessActivityClient';
import useInfiniteScroll from '../../../components/hooks/use-scroll';
import { Spinner } from '../../../components/spinner';
import { FitnessActivity } from './fitness-activity';
import { injectTSDI } from '../../../core/tsdi';
import { Api } from '../../../core/api';
import { EmployeeContext } from '../index';
import { AddActivity } from './add-activity';

export const EmployeeFitnessActivities = observer(() => {
    const employee = React.useContext(EmployeeContext);
    const { fitnessActivityClient } = injectTSDI(Api);
    const [searchParams, dispatch] = React.useReducer(useSearchReducer, {
        size: 30,
        page: 0,
        sort: '-timestamp'
    });

    const [fitnessActivities, setFitnessFitnessActivities] = React.useState<
        ActivityDto[]
    >([]);

    const fitnessActivityRequest = useWrapRequest(
        async (searchParams: QueryParamsDto, activities: ActivityDto[]) => {
            const result =
                await fitnessActivityClient.admin.activitySupportControllerGetActivities(
                    { userId: employee.mySportsId!, ...searchParams }
                );
            setFitnessFitnessActivities([
                ...activities,
                ...result.data.content
            ]);

            return { ...result.data, content: result.data.content || [] };
        },
        {}
    );

    const page = useInfiniteScroll({
        loading: fitnessActivityRequest.loading,
        distance: 200
    });

    React.useEffect(() => {
        if (
            page > 0 &&
            fitnessActivityRequest.$.totalPages &&
            fitnessActivityRequest.$.totalPages > page
        ) {
            dispatch({ type: 'page', page });
        }
    }, [page]);

    React.useEffect(() => {
        fitnessActivityRequest.request([searchParams, fitnessActivities]);
    }, [searchParams]);

    const refresh = () => {
        setFitnessFitnessActivities([]);
        dispatch({ type: 'page', page: 0 });
        fitnessActivityRequest.request([{ ...searchParams, page: 0 }, []]);
    };

    return (
        <>
            <AddActivity onChange={refresh} />
            {fitnessActivities.length === 0 && (
                <Alert variant="info">
                    <AlertDescription>No activities found</AlertDescription>
                </Alert>
            )}
            {fitnessActivities.length > 0 && (
                <ul className="divide-y divide-border rounded-xl border bg-card">
                    {fitnessActivities.map((fitnessActivity) => (
                        <FitnessActivity
                            key={fitnessActivity.id}
                            fitnessActivity={fitnessActivity}
                            onChange={refresh}
                        />
                    ))}
                </ul>
            )}
            {fitnessActivityRequest?.loading && (
                <div className="flex justify-center py-6">
                    <Spinner />
                </div>
            )}
        </>
    );
});

export default EmployeeFitnessActivities;
